// =============================================================================
// Origin UI — src/pages/compliance/ComplianceDashboard.tsx
// CM-11 — the Compliance team dashboard (Compliance role only).
//   - Completion matrix: pick an attestation → who's done / outstanding,
//     % complete, filter by office/department/status/reminder/employee name,
//     drill into a person's form, CSV export.
//   - Declarations log: all Gift / COI submissions, searchable, drill-in.
// Self-guards on the Compliance role (route does not depend on ProtectedRoute
// knowing the role).
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { complianceApi } from '@/services/complianceApi';
import type {
  ComplianceItemAdminListItem,
  ComplianceCompletion,
  ComplianceDeclarationLogItem,
} from '@/services/complianceApi';
import { SubmissionViewer } from '@/components/compliance/SubmissionViewer';
import styles from './compliance.module.css';

type DashTab = 'completion' | 'declarations';

function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const esc = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function ComplianceDashboard() {
  const { roles, rolesLoaded } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<DashTab>('completion');
  const [viewingSubmissionId, setViewingSubmissionId] = useState<string | null>(null);

  // ── Completion state ──
  const [attestations, setAttestations] = useState<ComplianceItemAdminListItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [completion, setCompletion] = useState<ComplianceCompletion | null>(null);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [officeFilter, setOfficeFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reminderFilter, setReminderFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  // ── Declarations state ──
  const [declarations, setDeclarations] = useState<ComplianceDeclarationLogItem[]>([]);
  const [declLoading, setDeclLoading] = useState(false);
  const [search, setSearch] = useState('');
  const declLoadedRef = useRef(false);

  const [error, setError] = useState<string | null>(null);

  // Load attestation list once.
  useEffect(() => {
    if (!roles.isComplianceExec) return;
    complianceApi
      .adminGetItems()
      .then((items) => {
        const att = items.filter((i) => i.itemType === 'Attestation');
        setAttestations(att);
        if (att.length > 0) setSelectedItemId(att[0].complianceItemId);
      })
      .catch((err) => {
        console.warn('Failed to load items', err);
        setError('Could not load attestations.');
      });
  }, [roles.isComplianceExec]);

  // Load completion when the selected attestation changes.
  useEffect(() => {
    if (!selectedItemId) return;
    let cancelled = false;
    setCompletionLoading(true);
    setOfficeFilter('');
    setDeptFilter('');
    setStatusFilter('');
    setReminderFilter('');
    setNameFilter('');
    complianceApi
      .adminGetCompletion(selectedItemId)
      .then((c) => {
        if (!cancelled) setCompletion(c);
      })
      .catch((err) => {
        console.warn('Failed to load completion', err);
        if (!cancelled) setError('Could not load completion data.');
      })
      .finally(() => {
        if (!cancelled) setCompletionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedItemId]);

  // Load declarations once, the first time the tab is opened.
  useEffect(() => {
    if (tab !== 'declarations' || declLoadedRef.current) return;
    declLoadedRef.current = true;
    setDeclLoading(true);
    complianceApi
      .adminGetDeclarationsLog()
      .then(setDeclarations)
      .catch((err) => {
        console.warn('Failed to load declarations', err);
        setError('Could not load declarations.');
      })
      .finally(() => setDeclLoading(false));
  }, [tab]);

  const offices = useMemo(
    () => Array.from(new Set((completion?.rows ?? []).map((r) => r.office))).sort(),
    [completion],
  );
  const departments = useMemo(
    () => Array.from(new Set((completion?.rows ?? []).map((r) => r.department))).sort(),
    [completion],
  );

  const filteredRows = useMemo(() => {
    let rows = completion?.rows ?? [];
    if (officeFilter) rows = rows.filter((r) => r.office === officeFilter);
    if (deptFilter) rows = rows.filter((r) => r.department === deptFilter);
    if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
    if (reminderFilter) rows = rows.filter((r) => r.reminderStatus === reminderFilter);
    if (nameFilter.trim()) {
      const q = nameFilter.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.employeeName.toLowerCase().includes(q) ||
          r.employeeNumber.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [completion, officeFilter, deptFilter, statusFilter, reminderFilter, nameFilter]);

  const filteredDeclarations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return declarations;
    return declarations.filter((d) =>
      [d.employeeName, d.employeeNumber, d.code, d.title, d.department, d.office]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [declarations, search]);

  const percent =
    completion && completion.totalAssigned > 0
      ? Math.round((completion.completedCount / completion.totalAssigned) * 100)
      : 0;

  function exportCompletionCsv() {
    if (!completion) return;
    downloadCsv(
      `${completion.code}_completion.csv`,
      ['Employee Number', 'Name', 'Department', 'Office', 'Status', 'Completed', 'Reminder Status', 'Last Reminder'],
      filteredRows.map((r) => [
        r.employeeNumber,
        r.employeeName,
        r.department,
        r.office,
        r.status,
        r.completedDate ? fmtDate(r.completedDate) : '',
        r.reminderStatus ?? 'None',
        r.lastReminderDate ? fmtDate(r.lastReminderDate) : '',
      ]),
    );
  }

  // ── Guards ──
  if (!rolesLoaded) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading&hellip;</div>
      </div>
    );
  }
  if (!roles.isComplianceExec) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Not authorised</h1>
          <p className={styles.subtitle}>
            The Compliance dashboard is available to Compliance team members only.
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Compliance dashboard</h1>
            <p className={styles.subtitle}>Completion tracking and declarations log.</p>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={() => navigate('/compliance/admin/content')}>
              Manage content
            </button>
            <button className={styles.secondaryBtn} onClick={() => navigate('/compliance/admin/articles')}>
              Manage news
            </button>
          </div>
        </div>
      </header>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'completion' ? styles.tabActive : ''}`}
          onClick={() => setTab('completion')}
        >
          Completion
        </button>
        <button
          className={`${styles.tab} ${tab === 'declarations' ? styles.tabActive : ''}`}
          onClick={() => setTab('declarations')}
        >
          Declarations
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* ── Completion matrix ── */}
      {tab === 'completion' && (
        <div className={styles.card}>
          {attestations.length === 0 ? (
            <div className={styles.emptyState}>No attestations have been created yet.</div>
          ) : (
            <>
              <div className={styles.toolbar}>
                <select
                  className={styles.filterSelect}
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                >
                  {attestations.map((a) => (
                    <option key={a.complianceItemId} value={a.complianceItemId}>
                      {a.title}
                    </option>
                  ))}
                </select>

                <select
                  className={styles.filterSelect}
                  value={officeFilter}
                  onChange={(e) => setOfficeFilter(e.target.value)}
                >
                  <option value="">All offices</option>
                  {offices.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>

                <select
                  className={styles.filterSelect}
                  value={deptFilter}
                  onChange={(e) => setDeptFilter(e.target.value)}
                >
                  <option value="">All departments</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                <select
                  className={styles.filterSelect}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="Completed">Completed</option>
                  <option value="Outstanding">Outstanding</option>
                </select>

                <select
                  className={styles.filterSelect}
                  value={reminderFilter}
                  onChange={(e) => setReminderFilter(e.target.value)}
                >
                  <option value="">All reminders</option>
                  <option value="None">No reminder sent</option>
                  <option value="Reminded">Reminded</option>
                  <option value="Escalated">Escalated</option>
                </select>

                <button className={styles.secondaryBtn} onClick={exportCompletionCsv}>
                  Export CSV
                </button>
              </div>

              <div className={styles.toolbar}>
                <input
                  className={styles.searchInput}
                  placeholder="Search by employee name or number…"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                />
              </div>

              {completionLoading || !completion ? (
                <div className={styles.loading}>Loading&hellip;</div>
              ) : (
                <>
                  <div className={styles.statGrid}>
                    <div className={styles.statCard}>
                      <span className={`${styles.statCardValue} ${styles.statCardValueAccent}`}>{percent}%</span>
                      <span className={styles.statCardLabel}>Complete</span>
                      <div className={styles.progressTrack}>
                        <div className={styles.progressFill} style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statCardValue}>{completion.completedCount}</span>
                      <span className={styles.statCardLabel}>Completed</span>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statCardValue}>
                        {completion.totalAssigned - completion.completedCount}
                      </span>
                      <span className={styles.statCardLabel}>Outstanding</span>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statCardValue}>{completion.totalAssigned}</span>
                      <span className={styles.statCardLabel}>Assigned</span>
                    </div>
                  </div>

                  {filteredRows.length === 0 ? (
                    <div className={styles.emptyState}>No people match these filters.</div>
                  ) : (
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Department</th>
                          <th>Office</th>
                          <th>Status</th>
                          <th>Reminder</th>
                          <th>Completed</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((r) => (
                          <tr key={r.employeeId}>
                            <td>
                              <div className={styles.cellName}>{r.employeeName}</div>
                              <div className={styles.cellSub}>{r.employeeNumber}</div>
                            </td>
                            <td>{r.department}</td>
                            <td>{r.office}</td>
                            <td>
                              <span
                                className={`${styles.statusBadge} ${
                                  r.status === 'Completed'
                                    ? styles.statusDone
                                    : styles.statusOutstanding
                                }`}
                              >
                                {r.status}
                              </span>
                            </td>
                            <td>
                              {r.status === 'Outstanding' && r.reminderStatus !== 'None' ? (
                                <span
                                  className={`${styles.statusBadge} ${
                                    r.reminderStatus === 'Escalated'
                                      ? styles.statusEscalated
                                      : styles.statusReminded
                                  }`}
                                  title={r.lastReminderDate ? fmtDate(r.lastReminderDate) : undefined}
                                >
                                  {r.reminderStatus === 'Escalated' ? 'Escalated' : 'Reminded'}
                                  {r.lastReminderDate && (
                                    <span style={{ fontWeight: 400, marginLeft: 4, fontSize: '0.85em' }}>
                                      {fmtDate(r.lastReminderDate)}
                                    </span>
                                  )}
                                </span>
                              ) : r.status === 'Completed' ? (
                                <span style={{ color: '#999' }}>—</span>
                              ) : (
                                <span style={{ color: '#999' }}>None</span>
                              )}
                            </td>
                            <td>{fmtDate(r.completedDate)}</td>
                            <td>
                              {r.status === 'Completed' && r.complianceSubmissionId && (
                                <button
                                  className={styles.linkBtn}
                                  onClick={() => setViewingSubmissionId(r.complianceSubmissionId!)}
                                >
                                  View
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Declarations log ── */}
      {tab === 'declarations' && (
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <input
              className={styles.searchInput}
              placeholder="Search by name, type, department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {declLoading ? (
            <div className={styles.loading}>Loading&hellip;</div>
          ) : filteredDeclarations.length === 0 ? (
            <div className={styles.emptyState}>No declarations found.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Declaration</th>
                  <th>Department</th>
                  <th>Office</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredDeclarations.map((d) => (
                  <tr key={d.complianceSubmissionId}>
                    <td>{fmtDate(d.submittedDate)}</td>
                    <td>
                      <div className={styles.cellName}>{d.employeeName}</div>
                      <div className={styles.cellSub}>{d.employeeNumber}</div>
                    </td>
                    <td>
                      <span className={styles.codeBadge}>{d.code}</span> {d.title}
                    </td>
                    <td>{d.department}</td>
                    <td>{d.office}</td>
                    <td>
                      <button
                        className={styles.linkBtn}
                        onClick={() => setViewingSubmissionId(d.complianceSubmissionId)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {viewingSubmissionId && (
        <SubmissionViewer
          submissionId={viewingSubmissionId}
          onClose={() => setViewingSubmissionId(null)}
        />
      )}
    </div>
  );
}
